import { useRef, useState, useEffect } from 'react';

function useScrollReveal(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setIsVisible(true); observer.unobserve(el); } },
      { threshold }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);
  return { ref, isVisible };
}

function RevealSection({ children, className = '', delay = 0 }: { children: React.ReactNode; className?: string; delay?: number }) {
  const { ref, isVisible } = useScrollReveal();
  return (
    <div ref={ref} className={`transition-all duration-700 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'} ${className}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
}

const cases = [
  {
    company: 'Agência Criativa',
    industry: 'Marketing Digital',
    logo: 'AC',
    result: '+340%',
    metric: 'aumento em vendas via WhatsApp',
    before: 'Disparos manuais, sem métricas',
    after: '5.000+ mensagens/mês automatizadas',
    highlight: 'ROI de 8x em 3 meses',
  },
  {
    company: 'E-commerce RJ',
    industry: 'Varejo Online',
    logo: 'ER',
    result: '12.847',
    metric: 'vendas diretas via campanhas',
    before: 'Apenas e-mail marketing (2% conversão)',
    after: 'WhatsApp + e-mail (14% conversão)',
    highlight: 'Ticket médio +65%',
  },
  {
    company: 'Imobiliária Lemos',
    industry: 'Imobiliário',
    logo: 'IL',
    result: '+220%',
    metric: 'agendamentos de visitas',
    before: 'Ligações frias, 8% de retorno',
    after: 'Campanhas segmentadas, 35% de retorno',
    highlight: '12 vendas diretas/mês',
  },
];

export default function LandingSuccessCases() {
  return (
    <section id="cases" className="py-16 md:py-24 px-5 md:px-8 bg-background-100 dark:bg-background-100">
      <div className="max-w-7xl mx-auto">
        <RevealSection>
          <div className="text-center mb-14">
            <span className="inline-block text-xs font-bold text-accent-600 dark:text-accent-400 uppercase tracking-[0.2em] mb-3">Cases de Sucesso</span>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 dark:text-foreground-100 mb-4">
              Resultados reais de<br />empresas como a sua
            </h2>
            <p className="text-foreground-500 dark:text-foreground-400 max-w-xl mx-auto text-base">
              Veja como empresas transformaram sua comunicação com o WZap PRO.
            </p>
          </div>
        </RevealSection>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {cases.map((c, i) => (
            <RevealSection key={i} delay={i * 120}>
              <div className="bg-white dark:bg-background-50 rounded-2xl border border-background-200 dark:border-background-300 p-6 md:p-7 h-full flex flex-col group hover:border-accent-200 dark:hover:border-accent-500/30 transition-all duration-300">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-accent-100 to-accent-200 dark:from-accent-500/20 dark:to-accent-500/30 flex items-center justify-center">
                    <span className="text-xs font-bold text-accent-700 dark:text-accent-300">{c.logo}</span>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground-900 dark:text-foreground-100">{c.company}</p>
                    <p className="text-[11px] text-foreground-500">{c.industry}</p>
                  </div>
                </div>

                <div className="mb-5">
                  <span className="text-3xl md:text-4xl font-heading font-extrabold text-accent-500">{c.result}</span>
                  <p className="text-sm text-foreground-600 dark:text-foreground-400 mt-1">{c.metric}</p>
                </div>

                <div className="space-y-3 mb-5 flex-1">
                  <div className="flex items-start gap-2.5">
                    <div className="w-5 h-5 rounded-full bg-red-100 dark:bg-red-500/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-arrow-down-line text-red-500 text-[10px]" />
                    </div>
                    <p className="text-xs text-foreground-500 leading-relaxed"><strong className="text-foreground-700 dark:text-foreground-300">Antes:</strong> {c.before}</p>
                  </div>
                  <div className="flex items-start gap-2.5">
                    <div className="w-5 h-5 rounded-full bg-emerald-100 dark:bg-emerald-500/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-arrow-up-line text-emerald-500 text-[10px]" />
                    </div>
                    <p className="text-xs text-foreground-500 leading-relaxed"><strong className="text-foreground-700 dark:text-foreground-300">Depois:</strong> {c.after}</p>
                  </div>
                </div>

                <div className="pt-4 border-t border-background-200 dark:border-background-300">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-accent-50 dark:bg-accent-500/10 text-accent-700 dark:text-accent-400 text-[11px] font-bold">
                    <i className="ri-flashlight-fill text-[10px]" /> {c.highlight}
                  </span>
                </div>
              </div>
            </RevealSection>
          ))}
        </div>
      </div>
    </section>
  );
}